import { useCallback, useEffect, useState } from 'react'

import { useLocalStorage } from 'usehooks-ts'

import { getBroadcastChannel } from 'src/hooks/use-broadcast-channel-listener'
import { BroadcastChannelMessageType, LocalStorageKey } from 'src/types'

export enum LightnessMode {
  Dark = 'dark',
  Light = 'light',
  System = 'system',
}

const DarkModeMediaQuery = '(prefers-color-scheme: dark)'

const getSystemLightnessMode = () =>
  window.matchMedia(DarkModeMediaQuery).matches ? LightnessMode.Dark : LightnessMode.Light

const getIsDarkFromLightnessMode = (lightnessMode: LightnessMode) => {
  let isDark = lightnessMode === LightnessMode.Dark
  if (lightnessMode === LightnessMode.System) {
    isDark = getSystemLightnessMode() === LightnessMode.Dark
  }
  return isDark
}

const setDarkModeClassOnDocument = (isDark: boolean) => {
  document.documentElement.classList.toggle('dark', isDark)
}

export const useLightnessMode = () => {
  const [lightnessMode, _setLightnessMode] = useLocalStorage(
    LocalStorageKey.LightnessMode,
    LightnessMode.System
  )

  const setLightnessMode = useCallback(
    (value: LightnessMode) => {
      _setLightnessMode(value)

      const isDark = getIsDarkFromLightnessMode(value)
      setDarkModeClassOnDocument(isDark)

      const broadcastChannel = getBroadcastChannel()
      broadcastChannel?.postMessage({
        type: BroadcastChannelMessageType.ChangeLightnessMode,
        value,
      })
    },
    [_setLightnessMode]
  )

  const [rerenderToggle, setRerenderToggle] = useState(false)
  useEffect(() => {
    // Listen for changes in system preferences
    const mediaQuery = window.matchMedia(DarkModeMediaQuery)
    const handleChange = () => {
      if (lightnessMode === LightnessMode.System) {
        const isDark = getSystemLightnessMode() === LightnessMode.Dark
        setDarkModeClassOnDocument(isDark)
        setRerenderToggle(!rerenderToggle) // Trigger re-render for components consuming isDarkMode state
      }
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [lightnessMode, rerenderToggle])

  useEffect(() => {
    const channel = getBroadcastChannel()
    if (!channel) {
      return
    }

    const listener = (e: MessageEvent) => {
      if (e.source === self) {
        return
      }
      if (e.data?.type === BroadcastChannelMessageType.ChangeLightnessMode) {
        _setLightnessMode(e.data.value)
        const isDark = getIsDarkFromLightnessMode(e.data.value)
        setDarkModeClassOnDocument(isDark)
      }
    }

    channel.addEventListener('message', listener)
    return () => channel.removeEventListener('message', listener)
  }, [_setLightnessMode, rerenderToggle])

  return {
    isDarkMode:
      lightnessMode === LightnessMode.Dark ||
      (lightnessMode === LightnessMode.System && getSystemLightnessMode() === LightnessMode.Dark),
    getSystemLightnessMode,
    lightnessMode,
    setLightnessMode,
  }
}
